import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilterContextProvider, useTableFilterContext } from 'features/TableFilter';
import { useGetPaymentsStatisticsTotalQuery } from 'entities/Warehouse';
import { PaymentsStatisticsFilter } from './PaymentsStatisticsFilter';
import { PaymentsStatisticsCards } from './PaymentsStatisticsCards';
import { transformPaymentsStatisticsFilters } from '../utils/transformPaymentsStatisticsFilters';
import { Button } from 'shared/ui/Button';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import axios from 'axios';
import { getAccessToken } from 'entities/User';
import { API_URL } from 'app/config/envVariables';
import { showNotification } from 'app/providers/NotificationsProvider';

const PaymentsStatisticsPageContent: FC = memo(() => {
  const { t } = useAppTranslation('common');
  const { appliedFilters, setFiltersOpened, tags, clearAllFilters } = useTableFilterContext();
  const [isExporting, setIsExporting] = useState(false);

  const queryParams = useMemo(() => {
    return transformPaymentsStatisticsFilters(appliedFilters);
  }, [appliedFilters]);

  const { data, isLoading } = useGetPaymentsStatisticsTotalQuery(queryParams);

  const openFilters = useCallback(() => {
    setFiltersOpened(true);
  }, [setFiltersOpened]);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);
      const token = getAccessToken();

      const response = await axios.post(
        `${API_URL}/warehouses/statistics/payments/export`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: queryParams,
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], {
        type: response.data.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'payments-statistics.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('info', t('Success'), t('Downloaded!'));
    } catch (error) {
      console.error('Error downloading the file', error);
      showNotification('error', t('Error'), t('Error when downloading data'));
    } finally {
      setIsExporting(false);
    }
  }, [queryParams, t]);

  const hasAppliedFilters = useMemo(() => {
    return Object.keys(tags).some((key) => tags[key].value != null);
  }, [tags]);

  const tagList = useMemo(() => {
    return Object.entries(tags)
      .filter(([_, tag]) => tag.value != null)
      .map(([key, tag]) => (
        <div key={key} className="flex items-center px-3 py-3 bg-accentLight rounded-xl">
          <div className="flex flex-col">
            <span className="text-xs text-accent">{tag.title}</span>
            <span className="text-sm text-accent">{tag.label}</span>
          </div>
          <CloseIcon className="ml-1.5 [&>path]:stroke-accent cursor-pointer" width={25} height={25} onClick={tag.onDelete} />
        </div>
      ));
  }, [tags]);

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Payments Statistics')}</div>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Button
          icon={<ExportIcon />}
          iconPosition="prev"
          theme="secondary"
          onClick={handleExport}
          className="max-w-fit"
          isLoading={isExporting}
        >
          {t('Export')}
        </Button>
        <Button icon={<FiltersIcon />} iconPosition="prev" theme="secondary" onClick={openFilters} className="max-w-fit">
          {t('Filters')}
        </Button>
        {hasAppliedFilters && (
          <div className="flex flex-wrap items-center gap-2">
            {tagList}
            <div className="text-error text-sm font-semibold cursor-pointer" onClick={clearAllFilters}>
              {t('Clear All')}
            </div>
          </div>
        )}
      </div>
      <PaymentsStatisticsFilter />
      <PaymentsStatisticsCards data={data} isLoading={isLoading} />
    </div>
  );
});

export const PaymentsStatisticsPage: FC = memo(() => {
  return (
    <TableFilterContextProvider>
      <PaymentsStatisticsPageContent />
    </TableFilterContextProvider>
  );
});
